import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Chip } from "@/components/chip";
import { DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { type RoadmapItem, useUpdateRoadmapItem } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import { cn } from "@/lib/utils";

import { EditPencil, Failure, FormRow, useFocusBack } from "./details-parts";
import { PrioritySelect, TEXTAREA_CLASS } from "./item-form";
import { StepButtons } from "./step-buttons";
import {
  contentEditKeys,
  type ItemDraft,
  itemDraft,
  itemUpdate,
  PRIORITY_TONE,
  SUMMARY_MAX,
  TITLE_MAX,
} from "./utils";

interface DetailsHeaderProps {
  item: RoadmapItem;
  pastMonth: boolean;
  editing: boolean;
  onEditing: (on: boolean) => void;
}

/**
 * The top of an item's details: the priority, the title (the dialog's name)
 * and the summary (its description). A pencil edits them in place, only what
 * the viewer may change (`can`), and says so in its name: an admin on an item
 * in progress gets "Prioriteit wijzigen". Never from a past month.
 */
export function DetailsHeader({ item, pastMonth, editing, onEditing }: DetailsHeaderProps) {
  const { t } = useTranslation();
  const pencil = useFocusBack(editing);
  const keys = contentEditKeys(item.can);
  const canEdit = !pastMonth && keys !== null;
  return (
    <DialogHeader className="space-y-0">
      {editing ? (
        <>
          {/* The dialog keeps its name while the form is open. */}
          <DialogTitle className="sr-only">{item.title}</DialogTitle>
          <DialogDescription className="sr-only">
            {t(keys?.editing ?? "roadmap.details.editingContent")}
          </DialogDescription>
          <ContentForm item={item} onClose={() => onEditing(false)} />
        </>
      ) : (
        <div className="space-y-1.5">
          <Chip tone={PRIORITY_TONE[item.priority] ?? "neutral"}>
            {t(`roadmap.priorityLong.${item.priority}`)}
          </Chip>
          <div className="flex items-start gap-1">
            <DialogTitle className="min-w-0 pt-1 leading-tight">{item.title}</DialogTitle>
            {canEdit && (
              <EditPencil ref={pencil} label={t(keys.pencil)} onClick={() => onEditing(true)} />
            )}
          </div>
          <DialogDescription className={cn("max-w-prose", item.summary && "whitespace-pre-line")}>
            {item.summary || t("roadmap.details.noSummary")}
          </DialogDescription>
        </div>
      )}
    </DialogHeader>
  );
}

interface ContentFormProps {
  item: RoadmapItem;
  /** Back to the header: after a save, or on cancel. */
  onClose: () => void;
}

/**
 * The priority, the title and the summary as a form, only what the viewer
 * may change. Save sends only what the viewer changed here; a field left
 * alone follows the item, so a change saved meanwhile is never sent back.
 * The deadline lives in Planning.
 */
function ContentForm({ item, onClose }: ContentFormProps) {
  const { t } = useTranslation();
  const ids = useId();
  const update = useUpdateRoadmapItem();
  // Only what the viewer changed here; the rest follows the item as it is now.
  const [edits, setEdits] = useState<Partial<ItemDraft>>({});
  const draft = itemDraft(item, edits);
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const { can } = item;
  const change = itemUpdate(item, { ...draft, deadline: item.deadline ?? null }, can);
  const titled = !can.edit_text || draft.title.trim() !== "";

  const save = async () => {
    if (!change || !titled || update.isPending) {
      return;
    }
    setFailure(null);
    try {
      await update.mutateAsync({ itemId: item.id, change });
      onClose();
    } catch (error) {
      setFailure(inlineFailure({ error }));
    }
  };

  return (
    <form
      className="space-y-3"
      data-section="content-form"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      {can.edit_priority && (
        <FormRow label={t("roadmap.form.priority")} htmlFor={`${ids}-priority`}>
          <PrioritySelect
            id={`${ids}-priority`}
            value={draft.priority}
            onChange={(priority) => setEdits({ ...edits, priority })}
            // Without a title to edit, the pencil's form starts on the priority.
            autoFocus={!can.edit_text}
          />
        </FormRow>
      )}
      {can.edit_text && (
        <>
          <FormRow label={t("roadmap.form.title")} htmlFor={`${ids}-title`}>
            <Input
              id={`${ids}-title`}
              value={draft.title}
              maxLength={TITLE_MAX}
              // Written in the item's language, as it was added.
              lang={item.language}
              onChange={(event) => setEdits({ ...edits, title: event.target.value })}
              aria-invalid={!titled || undefined}
              className="h-9 font-semibold pointer-coarse:h-10"
              autoFocus
            />
            {!titled && (
              <p className="text-destructive mt-1 text-xs">{t("roadmap.form.titleRequired")}</p>
            )}
          </FormRow>
          <FormRow label={t("roadmap.details.summary")} htmlFor={`${ids}-summary`}>
            <textarea
              id={`${ids}-summary`}
              value={draft.summary}
              maxLength={SUMMARY_MAX}
              rows={4}
              lang={item.language}
              onChange={(event) => setEdits({ ...edits, summary: event.target.value })}
              className={TEXTAREA_CLASS}
            />
          </FormRow>
        </>
      )}
      {failure && <Failure>{apiErrorMessage(failure.error, "updateRoadmapItem")}</Failure>}
      <StepButtons
        onBack={onClose}
        confirm={t("common.save")}
        pending={update.isPending}
        disabled={!change || !titled}
      />
    </form>
  );
}
